import { Horizontal } from '@eventbrite/eds-layout';
import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import TicketImageThumbnail from '../../ticketImageThumbnail';
import TicketDonationFeeTaxDescription from '../TicketDonationFeeTaxDescription';
import TicketSalesEndDate, {
    TicketSalesEndDateProps,
} from '../TicketSalesEndDate';

import { TicketAvailabilityFieldProps } from '../../../containers/ticketSelection/TicketAvailabilityField';
import { TicketStatus } from '../../../models/tickets';
import { UrgencySignals } from '../../../models/urgencySignals';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import { TicketCommonProps, TicketMappedImage } from '../constants';
import PromoCodePills from '../PromoCodePills';
import { TicketContentProps } from '../TicketContent';
import { TicketDisplayCardContentFullSizePrice } from '../TicketDisplayCardContentFullSize/TicketDisplayCardContentFullSizePrice';
import { TicketPriceProps } from '../TicketPrice/TicketPrice';
import { TicketQuantityPickerProps } from '../TicketQuantityPicker';
import TicketRemainingUrgencySignal from '../TicketRemainingTicketsCount/TicketRemainingUrgencySignal';
import { getIsCardDisabledByStatus } from '../utils';
import defaultStyles from './TicketPriceCountDateAndThumbnail.module.scss';
import rebrandStyles from './TicketPriceCountDateAndThumbnail.rebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

export interface TicketPriceCountDateAndThumbnailProps
    extends Pick<
            TicketPriceProps,
            'feesAndTaxes' | 'price' | 'priceWithoutDiscounts'
        >,
        Pick<
            TicketSalesEndDateProps,
            'formattedSalesEndDate' | 'hideSaleDates'
        >,
        Pick<TicketQuantityPickerProps, 'isDonation' | 'ticketStatus'>,
        Pick<
            TicketContentProps,
            | 'category'
            | 'cost'
            | 'currencyFormat'
            | 'hideSelect'
            | 'includeFee'
            | 'includeTax'
        >,
        Pick<TicketAvailabilityFieldProps, 'showJoinWaitlist'> {
    image?: TicketMappedImage;
    isAppRebrandActive?: boolean;
    isBOGOTicket: boolean;
    remainingQuantity: TicketCommonProps['quantityRemaining'];
    urgencySignals: UrgencySignals | null;
    showUnlockedPill: boolean;
    showDiscountedPill: boolean;
}

const TicketPriceCountDateAndThumbnail: FunctionComponent<
    TicketPriceCountDateAndThumbnailProps
> = ({
    category,
    cost,
    currencyFormat,
    feesAndTaxes,
    formattedSalesEndDate,
    hideSelect,
    hideSaleDates,
    image,
    includeFee,
    includeTax,
    isAppRebrandActive = false,
    isBOGOTicket,
    isDonation,
    price,
    priceWithoutDiscounts,
    remainingQuantity,
    showDiscountedPill,
    showJoinWaitlist,
    showUnlockedPill,
    ticketStatus,
    urgencySignals,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    const isSoldOut = ticketStatus === TicketStatus.soldOut;
    const isCardDisabled =
        getIsCardDisabledByStatus(ticketStatus) && !showJoinWaitlist;
    const canBeSelected = !hideSelect && !isCardDisabled;
    const ticketPrice = !isDonation ? (
        <TicketDisplayCardContentFullSizePrice
            cost={cost}
            currencyFormat={currencyFormat}
            feesAndTaxes={feesAndTaxes}
            isAppRebrandActive={isAppRebrandActive}
            isBOGO={isBOGOTicket}
            isDisabled={isCardDisabled}
            price={price}
            priceWithoutDiscounts={priceWithoutDiscounts}
        />
    ) : null;

    const isDiscounted = showDiscountedPill && !!cost && !isDonation;

    const promoCodePills = (
        <PromoCodePills
            isBOGO={isBOGOTicket}
            isDiscounted={isDiscounted}
            isUnlocked={showUnlockedPill}
            className={classNames(styles.promoCodePills, {
                'eds-l-pad-top-2': !!ticketPrice,
            })}
            isAppRebrandActive={isAppRebrandActive}
        />
    );

    const getTicketsRemainingUrgencySignalPill = (className?: string) => (
        <TicketRemainingUrgencySignal
            className={className}
            isAppRebrandActive={isAppRebrandActive}
            isSoldOut={isSoldOut}
            numTicketsRemaining={remainingQuantity}
            urgencySignals={urgencySignals}
        />
    );

    const ticketsRemainingUrgencySignalPillWithImageClassName =
        isAppRebrandActive ? 'eds-l-mar-top-2' : 'eds-l-mar-top-4';

    const ticketsRemainingUrgencySignalPill = showJoinWaitlist
        ? null
        : image
        ? getTicketsRemainingUrgencySignalPill(
              ticketsRemainingUrgencySignalPillWithImageClassName,
          )
        : getTicketsRemainingUrgencySignalPill();

    const salesEndDateAndDonationFeeTaxDescription = (
        <div className="eds-l-pad-top-1">
            <TicketSalesEndDate
                formattedSalesEndDate={formattedSalesEndDate}
                hideSaleDates={hideSaleDates}
                isAppRebrandActive={isAppRebrandActive}
                ticketStatus={ticketStatus}
            />
            <TicketDonationFeeTaxDescription
                canBeSelected={canBeSelected}
                category={category}
                includeFee={includeFee}
                includeTax={includeTax}
                isAppRebrandActive={isAppRebrandActive}
                separatorCharacter=" - "
            />
        </div>
    );

    return image ? (
        <Horizontal
            className="eds-l-pad-top-4 eds-l-pad-hor-4"
            data-testid="ticket-price-count-date-and-thumbnail"
        >
            <div className={styles.column}>
                {ticketPrice}
                {promoCodePills}
                {salesEndDateAndDonationFeeTaxDescription}
                {ticketsRemainingUrgencySignalPill}
            </div>
            <div className={styles.thumbnail}>
                <TicketImageThumbnail
                    fullImageUrl={image.fullSizeUrl}
                    isAppRebrandActive={isAppRebrandActive}
                    thumbnailUrl={image.thumbnailUrl}
                    title={image.ticketName}
                />
            </div>
        </Horizontal>
    ) : (
        <div className="eds-l-pad-hor-4" data-testid="ticket-price-count-date">
            <Horizontal className={classNames('eds-l-pad-top-4', styles.line)}>
                {ticketPrice}
                {ticketsRemainingUrgencySignalPill}
            </Horizontal>
            {promoCodePills}
            {salesEndDateAndDonationFeeTaxDescription}
        </div>
    );
};

export default TicketPriceCountDateAndThumbnail;
